import { isDate } from '@rainbow-modules/validation';
import { RenderIf } from 'react-rainbow-components';
import { ReactNode, useMemo } from 'react';
import { Timestamp } from 'firebase/firestore';
import defaultFormatter, { formatRelativeDate, getFormatter } from 'data/services/date/formatter';
import {
    StyledColumnWrapper,
    StyledDate,
    StyledColumnWrapperRelative,
    RelativeDateLabel,
    RelativeDateDescription,
} from './styled';

interface DateTimeColumnProps {
    value?: Timestamp | Date | string;
    options?: Intl.DateTimeFormatOptions;
    showRelativeDate?: boolean;
    showDateInTimeZone?: {
        label: string;
        value?: string;
    };
    emptyLabel?: ReactNode;
}

const normalizeDate = (value: Timestamp | Date | string) => (
    value instanceof Timestamp
        ? new Date(value.toMillis())
        : new Date(value || '')
);

const isValidDate = (
    value: Date,
) => isDate(value) && !Number.isNaN(value.getTime());

const DateTimeColumn = (props: DateTimeColumnProps) => {
    const {
        value, options, emptyLabel = null, showRelativeDate = false, showDateInTimeZone,
    } = props;
    const formatter = useMemo(
        () => (
            options
                ? getFormatter('en-US', options)
                : defaultFormatter
        ),
        [options],
    );

    const dateValue = useMemo(() => {
        const date = normalizeDate(value || '');
        if (value && isValidDate(date)) {
            return formatter.format(date);
        }
        return null;
    }, [formatter, value]);

    const relativeDateValue = useMemo(() => {
        const date = normalizeDate(value || '');
        if (value && isValidDate(date)) {
            return formatRelativeDate(date);
        }
        return null;
    }, [value]);

    const dateInTimeZoneValue = useMemo(() => {
        if (showDateInTimeZone?.value) {
            const date = normalizeDate(value || '');
            if (value && isValidDate(date)) {
                const result = [getFormatter('en-US', {
                    timeZone: showDateInTimeZone.value || undefined,
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                }).format(date)];
                if (showDateInTimeZone?.label) {
                    result.push(`(${showDateInTimeZone?.label})`);
                }

                return result.join(' ');
            }
        }
        return undefined;
    }, [showDateInTimeZone, value]);

    if (!dateValue) return emptyLabel;

    if (showRelativeDate) {
        return (
            <StyledColumnWrapperRelative>
                <RelativeDateLabel>{relativeDateValue}</RelativeDateLabel>
                <RelativeDateDescription>{dateValue}</RelativeDateDescription>
                <RenderIf isTrue={dateInTimeZoneValue}>
                    <RelativeDateDescription>{dateInTimeZoneValue}</RelativeDateDescription>
                </RenderIf>
            </StyledColumnWrapperRelative>
        );
    }

    return (
        <StyledColumnWrapper>
            <StyledDate>{dateValue}</StyledDate>
            <RenderIf isTrue={dateInTimeZoneValue}>
                <RelativeDateDescription>{dateInTimeZoneValue}</RelativeDateDescription>
            </RenderIf>
        </StyledColumnWrapper>
    );
};

export default DateTimeColumn;
